/**
 * Column transformers for ScoopTable
 * Transforms abstracted column definitions to AG-Grid format
 */

/**
 * Transform a single ScoopTable column to AG-Grid column definition
 * @param {Object} column - ScoopTable column definition
 * @param {Object} [options] - Transform options
 * @param {number} [options.defaultWidth] - Default width for columns without explicit width
 * @returns {Object} AG-Grid column definition
 */
export const transformColumnToAgGrid = (column, options = {}) => {
    const { defaultWidth } = options

    const agGridCol = {
        field: column.field,
        // Support both ScoopTable format (header) and MUI DataGrid format (headerName)
        headerName: column.header || column.headerName || column.field,
        sortable: column.sortable !== false,
        resizable: column.resizable !== false,
        suppressMovable: column.movable === false,
    }

    // Width settings (column-specific takes precedence over default)
    if (column.width) agGridCol.width = column.width
    else if (defaultWidth) agGridCol.width = defaultWidth
    if (column.minWidth) agGridCol.minWidth = column.minWidth
    if (column.maxWidth) agGridCol.maxWidth = column.maxWidth
    if (column.flex) agGridCol.flex = column.flex

    // Alignment
    if (column.align) {
        agGridCol.cellStyle = { textAlign: column.align }
        agGridCol.headerClass = `ag-header-cell-${column.align}`
    }

    // Custom cell renderer
    if (column.render) {
        agGridCol.cellRenderer = (params) => {
            return column.render({
                value: params.value,
                row: params.data,
                rowIndex: params.rowIndex,
                column: column,
            })
        }
    }

    // Value getter
    if (column.valueGetter) {
        agGridCol.valueGetter = (params) => {
            return column.valueGetter({
                row: params.data,
                column: column,
            })
        }
    }

    // Value formatter
    if (column.valueFormatter) {
        agGridCol.valueFormatter = (params) => {
            return column.valueFormatter({
                value: params.value,
                row: params.data,
                column: column,
            })
        }
    }

    // Selection checkboxes
    if (column.checkboxSelection) {
        agGridCol.checkboxSelection = true
    }
    if (column.headerCheckboxSelection) {
        agGridCol.headerCheckboxSelection = true
    }

    // Hide column
    if (column.hidden) {
        agGridCol.hide = true
    }

    // Cell class
    if (column.cellClass) {
        agGridCol.cellClass = column.cellClass
    }

    // Cell style (can be object or function)
    if (column.cellStyle && !column.align) {
        agGridCol.cellStyle = column.cellStyle
    } else if (column.cellStyle && column.align) {
        // Merge align style with custom cellStyle
        agGridCol.cellStyle = (params) => {
            const baseStyle = { textAlign: column.align }
            const customStyle =
                typeof column.cellStyle === 'function' ? column.cellStyle(params) : column.cellStyle
            return { ...baseStyle, ...customStyle }
        }
    }

    // Custom header renderer (optional - only used when provided)
    if (column.headerRender) {
        agGridCol.headerComponent = 'customHeader'
        agGridCol.headerComponentParams = {
            headerRender: column.headerRender,
            column: column,
        }
    }

    return agGridCol
}

/**
 * Transform array of ScoopTable columns to AG-Grid column definitions
 * @param {Array} columns - Array of ScoopTable column definitions
 * @param {Object} [options] - Transform options
 * @param {number} [options.defaultWidth] - Default width for columns without explicit width
 * @returns {Array} Array of AG-Grid column definitions
 */
export const transformColumnsToAgGrid = (columns, options = {}) => {
    if (!columns || !Array.isArray(columns)) return []
    return columns.map((column) => transformColumnToAgGrid(column, options))
}

/**
 * Transform legacy ScoopTable column format to new format
 * Legacy format: { columnName: string, columnType: string }
 * New format: { field: string, header: string, align?: string }
 * @param {Object} legacyColumn - Legacy column definition
 * @param {number} index - Column index (used as field name)
 * @returns {Object} New column definition
 */
export const transformLegacyColumn = (legacyColumn, index) => {
    // Handle string columns (e.g., 'date')
    if (typeof legacyColumn === 'string') {
        return {
            field: `col_${index}`,
            header: legacyColumn === 'date' ? 'Date' : legacyColumn,
        }
    }

    const isNumeric = ['Integer', 'Decimal'].includes(legacyColumn.columnType)

    return {
        field: `col_${index}`,
        header: legacyColumn.columnName,
        align: isNumeric ? 'right' : 'left',
        type: legacyColumn.columnType,
    }
}

/**
 * Transform array of legacy columns to new format
 * @param {Array} legacyColumns - Array of legacy column definitions
 * @returns {Array} Array of new column definitions
 */
export const transformLegacyColumns = (legacyColumns) => {
    if (!legacyColumns || !Array.isArray(legacyColumns)) return []
    return legacyColumns.map(transformLegacyColumn)
}
